"use client"

import { useTranslations } from "next-intl"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/auth/use-auth"
import { useContactExclusionToasts } from "@/hooks/mutations/message-agent-exclusions"
import { UNIFIED_INBOX_QUERY_KEY } from "@/hooks/queries/unified-inbox"
import { scopedId } from "@/lib/inbox/scoped-id"
import type {
  ThreadActionTarget,
  ThreadMutation,
  ThreadMutationId,
} from "@/lib/conversation/thread-header"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  excludeConversationContactAction,
  includeConversationContactAction,
} from "@/server-actions/message-agent-exclusions"

/**
 * The ONE write in an otherwise read-only surface.
 *
 * Opening a messaging conversation here marks it read, exactly as opening it in
 * the agent's own inbox does. The alternative is two inboxes disagreeing about
 * unread state, which makes the unread filter useless in both. Email and voice
 * have no read state at all, so nothing else needs this.
 *
 * It goes through the agent inbox's existing endpoint rather than a new server
 * action: that endpoint already owns the tenant-and-agent ownership check, and a
 * second implementation of "mark read" is a second thing to keep correct.
 *
 * **Through `fetchWithAuth`, never a bare `fetch`.** The app\'s global fetch patch
 * attaches the Bearer token to server-action POSTs only; middleware reads it from
 * the `authorization` header alone, so a plain `fetch` at `/api/*` is a 401 every
 * time. Combined with the silent failure below, that is a write that never
 * happens and never says so.
 *
 * Failure is deliberately silent. The operator came to read a conversation, and
 * a toast about a bookkeeping write they never asked for would be noise; the
 * badge and the unread filter correct themselves on the next successful open.
 */
export function useMarkConversationReadMutation() {
  const queryClient = useQueryClient()
  const { fetchWithAuth } = useAuth()

  return useMutation({
    mutationFn: async ({ agentId, conversationId }: { agentId: string; conversationId: string }) => {
      const response = await fetchWithAuth(
        `/api/agents/messaging/${agentId}/conversations/${conversationId}/messages/read`,
        { method: "POST" },
      )
      if (!response.ok) throw new Error(`Failed to mark as read: ${response.status}`)
    },
    onSuccess: () => {
      // Only what this write changed. The whole prefix would also drop the
      // capabilities and the filter options, which cost a `DISTINCT` over the
      // tenant's calls to rebuild.
      queryClient.invalidateQueries({ queryKey: [...UNIFIED_INBOX_QUERY_KEY, "list"] })
      queryClient.invalidateQueries({
        queryKey: [...UNIFIED_INBOX_QUERY_KEY, "attention-summary"],
      })
    },
    onError: (error) => {
      console.error("[UnifiedInbox] Marking the conversation read failed:", error)
    },
  })
}

/** Whatever the performed endpoint answered with. Every field is optional because seven shapes share it. */
export interface ThreadActionResult {
  error?: string
  reason?: string
  /** The endpoint's own account of what it did. The AI reply and the CRM lead send one. */
  message?: string
  opportunity_id?: string
  external_url?: string
}

export interface ThreadActionInput {
  action: ThreadMutation
  /** The prefixed thread id, so the reload targets this conversation and not the world. */
  threadId: string
  /** The agent that owns the conversation. Its routes own the ownership checks. */
  agentId: string
  /** The source's own id — for messaging, the conversation uuid. */
  nativeId: string
  /** What the action acts on, where the conversation id is not enough. From the header. */
  target?: ThreadActionTarget
  /**
   * The message content, which only `classify_message` needs: its route reads
   * text rather than a stored record. Supplied by the caller, which already has
   * the transcript, rather than shipped a second time inside every header.
   */
  content?: { text: string; subject?: string }
}

/**
 * Performing what the header offers.
 *
 * Every write goes through the owning subsystem's own endpoint: those already
 * hold the tenant-and-agent ownership checks, and a second implementation of
 * «pause this conversation» is a second thing to keep correct. The service has
 * already dropped any action this caller may not perform, and the routes check
 * the permission again regardless — this hook only routes.
 *
 * Unlike the mark-read write, nothing here is silent. The operator pressed this
 * on purpose, and one toast per action is the whole feedback channel: a second
 * one somewhere else is how a failed exclusion came to be reported twice.
 */
export function useThreadActionMutation() {
  const ACTION_TOASTS = useThreadActionToasts()

  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()
  const { fetchWithAuth } = useAuth()

  return useMutation({
    mutationFn: async (input: ThreadActionInput): Promise<ThreadActionResult> => {
      return withQueryHandling(perform(input, fetchWithAuth), ACTION_TOASTS[input.action.id])
    },
    onSuccess: (_result, { threadId }) => {
      // This conversation and the list it sits in. Not the whole prefix: that
      // would also drop the capabilities and the filter options, which cost a
      // `DISTINCT` over the tenant's calls to rebuild.
      queryClient.invalidateQueries({ queryKey: [...UNIFIED_INBOX_QUERY_KEY, "thread", threadId] })
      queryClient.invalidateQueries({ queryKey: [...UNIFIED_INBOX_QUERY_KEY, "list"] })
      queryClient.invalidateQueries({
        queryKey: [...UNIFIED_INBOX_QUERY_KEY, "attention-summary"],
      })
    },
  })
}

function perform(
  { action, agentId, nativeId, target, content }: ThreadActionInput,
  fetchWithAuth: FetchWithAuth,
): Promise<ThreadActionResult> {
  const conversation = `/api/agents/messaging/${agentId}/conversations/${nativeId}`
  const conversationId = nativeId

  switch (action.id) {
    case "pause_ai":
      return send(fetchWithAuth, conversation, "PATCH", { ai_paused: true })
    case "resume_ai":
      return send(fetchWithAuth, conversation, "PATCH", { ai_paused: false })
    case "exempt_auto_pause":
      return send(fetchWithAuth, conversation, "PATCH", { auto_pause_exempt: true })
    case "clear_auto_pause_exemption":
      return send(fetchWithAuth, conversation, "PATCH", { auto_pause_exempt: false })
    case "new_session":
      return send(fetchWithAuth, `${conversation}/new-session`, "POST")
    // Through the server action the agent inbox already uses: it resolves the
    // agent from the conversation and rewrites the whole list atomically, which
    // a PATCH of `contact_exclusions` from here would have to redo.
    // Cast, not conversion: a server action answers with its view OR with an
    // `{ status, errorMessage }` envelope, and `withQueryHandling` has to see the
    // envelope intact to turn it into a rejection.
    case "exclude_contact":
      return excludeConversationContactAction({ conversationId }) as Promise<ThreadActionResult>
    case "include_contact":
      return includeConversationContactAction({ conversationId }) as Promise<ThreadActionResult>

    // The email agent acts per sender and per message, not per thread, so these
    // read the header's `target` rather than the conversation id.
    case "blacklist_sender":
      return send(fetchWithAuth, "/api/agents/email/blacklist", "POST", {
        email: target?.senderEmail,
        agent_id: agentId,
      })
    case "unblacklist_sender":
      return send(fetchWithAuth, "/api/agents/email/blacklist", "DELETE", {
        email: target?.senderEmail,
      })
    case "classify_message":
      return send(fetchWithAuth, "/api/agents/email/classify", "POST", {
        text: content?.text ?? "",
        subject: content?.subject,
        external_id: target?.messageExternalId,
        agent_id: agentId,
      })
    case "generate_ai_reply":
      return send(fetchWithAuth, "/api/agents/email/auto-reply/single", "POST", {
        email_id: target?.messageId,
        agent_id: agentId,
      })
    case "create_crm_lead":
      return send(fetchWithAuth, "/api/agents/email/crm-lead", "POST", {
        email_id: target?.messageId,
        agent_id: agentId,
      })
  }
}

function useThreadActionToasts(): Record<
  ThreadMutationId,
  { loading: string; success: string | ((result: ThreadActionResult) => string) }
> {
  const tUi = useTranslations("Feedback.ThreadActions")
  const CONTACT_EXCLUSION_TOASTS = useContactExclusionToasts()
  return {
  pause_ai: { loading: tUi("pausingAI"), success: tUi("aIPausedYouAreReplyingManually") },
  resume_ai: { loading: tUi("resumingAI"), success: tUi("aIIsAnsweringAgain") },
  exempt_auto_pause: {
    loading: tUi("updating"),
    success: tUi("thisConversationNowIgnoresPauseAIOnHuman"),
  },
  clear_auto_pause_exemption: {
    loading: tUi("updating"),
    success: tUi("thisConversationFollowsTheAgentSettingAgain"),
  },
  exclude_contact: CONTACT_EXCLUSION_TOASTS.exclude,
  include_contact: CONTACT_EXCLUSION_TOASTS.include,
  new_session: { loading: tUi("startingANewSession"), success: tUi("newSessionStarted") },
  blacklist_sender: {
    loading: tUi("blacklistingSender"),
    success: tUi("senderBlacklistedTheAgentWillNotReplyTo"),
  },
  unblacklist_sender: {
    loading: tUi("removingFromBlacklist"),
    success: tUi("senderRemovedTheAgentRepliesToThemAgain"),
  },
  classify_message: { loading: tUi("classifying"), success: tUi("messageClassified") },
  generate_ai_reply: {
    loading: tUi("writingAReply"),
    // The route answers 200 for every outcome — skipped, escalated, forwarded, a
    // dry run, a blacklisted sender — and says which in `message`. A fixed
    // «AI reply created» would report a success for each of them.
    success: (result) => result.message ?? tUi("aIReplyCreated"),
  },
  create_crm_lead: {
    loading: tUi("creatingACRMLead"),
    // «Existing opportunity found» and «Opportunity created successfully» are
    // different answers, and the second press is the one that needs telling apart.
    success: (result) => result.message ?? tUi("cRMLeadCreated"),
  },
}
}

/**
 * Replying to a messaging conversation, through the agent inbox's own endpoint —
 * the one that owns the ownership check, the channel adapter and the segmenting.
 *
 * No toast: the composer reports its own failure and keeps the draft, and a
 * success toast for a message the operator can see arrive is noise.
 */
export function useSendConversationMessageMutation() {
  const queryClient = useQueryClient()
  const { fetchWithAuth } = useAuth()

  return useMutation({
    mutationFn: async ({
      agentId,
      conversationId,
      text,
    }: {
      agentId: string
      conversationId: string
      text: string
    }) =>
      send(
        fetchWithAuth,
        `/api/agents/messaging/${agentId}/conversations/${conversationId}/messages`,
        "POST",
        { text },
      ),
    onSuccess: (_result, { conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: [...UNIFIED_INBOX_QUERY_KEY, "thread", scopedId("message", conversationId)],
      })
      queryClient.invalidateQueries({ queryKey: [...UNIFIED_INBOX_QUERY_KEY, "list"] })
    },
  })
}

type FetchWithAuth = ReturnType<typeof useAuth>["fetchWithAuth"]

/**
 * The parsed body is returned, not discarded: creating a CRM lead answers with
 * the opportunity's own URL, and the email table opens it.
 */
async function send(
  fetchWithAuth: FetchWithAuth,
  url: string,
  method: "PATCH" | "POST" | "DELETE",
  body?: Record<string, unknown>,
): Promise<ThreadActionResult> {
  const response = await fetchWithAuth(url, {
    method,
    ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  })
  const detail = (await response.json().catch(() => null)) as ThreadActionResult | null

  if (!response.ok) {
    // A CRM lead the provider declined to create comes back as an error with a
    // reason rather than a failure; the reason is what the operator needs either way.
    throw new Error(detail?.reason ?? detail?.error ?? `Request failed with ${response.status}`)
  }

  return detail ?? {}
}
